import type { DistributorWithRoutes, RepRequestStatus, RequestedCustomer } from './api';

/**
 * Presentation rules of the Requests tab, kept free of React so the client
 * tests can cover them: status wording for each audience, the open/history
 * split, expand/collapse state, decision availability, and form validation.
 * The server enforces every rule again; these only shape the screen.
 */

export type SupportRequestStatus = 'pending' | 'in_review' | 'flagged' | 'approved' | 'rejected';
export type RequestType = 'new_stop' | 'conflict';

/** What a rep reads; a flagged conflict is still simply "In review". */
export const REP_STATUS_LABEL: Record<RepRequestStatus, string> = {
  pending: 'Awaiting Xtreme review',
  in_review: 'In review',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const SUPPORT_STATUS_LABEL: Record<SupportRequestStatus, string> = {
  pending: 'Awaiting review',
  in_review: 'Match found: needs Tropicana review',
  flagged: 'Flagged with Tropicana',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const TYPE_LABEL: Record<RequestType, string> = {
  new_stop: 'New stop',
  conflict: 'Existing match',
};

const OPEN: readonly SupportRequestStatus[] = ['pending', 'in_review', 'flagged'];

export function isOpenStatus(status: SupportRequestStatus): boolean {
  return OPEN.includes(status);
}

/** The open queue or the decided history, newest first. */
export function partitionRequests<
  T extends { id: number; status: SupportRequestStatus; submittedAt: string },
>(requests: readonly T[], view: 'open' | 'history'): T[] {
  return sortNewestFirst(
    requests.filter((request) =>
      view === 'open' ? isOpenStatus(request.status) : !isOpenStatus(request.status),
    ),
  );
}

export function sortNewestFirst<T extends { id: number; submittedAt: string }>(
  requests: readonly T[],
): T[] {
  return [...requests].sort((a, b) => b.submittedAt.localeCompare(a.submittedAt) || b.id - a.id);
}

/** Rows start collapsed; toggling one never touches the others. */
export function toggleExpanded(expanded: ReadonlySet<number>, id: number): Set<number> {
  const next = new Set(expanded);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Mirror of the server's approval rule, used only to disable the button and explain why. */
export function approvalBlocker(request: {
  type: RequestType;
  status: SupportRequestStatus;
}): string | null {
  if (!isOpenStatus(request.status)) return 'This request has already been decided.';
  if (request.type === 'conflict' && request.status !== 'flagged') {
    return 'Flag this match with Tropicana and resolve it before approving.';
  }
  return null;
}

export function canFlag(request: { type: RequestType; status: SupportRequestStatus }): boolean {
  return request.type === 'conflict' && request.status === 'in_review';
}

export function canReject(request: { status: SupportRequestStatus }): boolean {
  return isOpenStatus(request.status);
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

export function fullAddressOf(customer: RequestedCustomer): string {
  const place = [customer.city, customer.state, customer.zipCode].filter(Boolean).join(' ');
  return [customer.streetAddress, place].filter(Boolean).join(', ');
}

export function phonesOf(record: {
  primaryPhone: string | null;
  secondaryPhone: string | null;
}): string {
  return [record.primaryPhone, record.secondaryPhone].filter(Boolean).join(' · ') || '—';
}

/* ---------- the Request New Stop form ---------- */

export interface NewStopFormValues {
  distributorId: string;
  routeId: string;
  name: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  contactName: string;
  primaryPhone: string;
  secondaryPhone: string;
  googlePlaceId?: string;
}

export type FormErrors = Partial<Record<keyof NewStopFormValues, string>>;

/** Pre-selects the distributor (and its route) when there is only one choice. */
export function emptyForm(distributors: readonly DistributorWithRoutes[]): NewStopFormValues {
  const only = distributors.length === 1 ? distributors[0] : undefined;
  return {
    distributorId: only ? String(only.id) : '',
    routeId: only && only.routes.length === 1 ? String(only.routes[0]!.id) : '',
    name: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    contactName: '',
    primaryPhone: '',
    secondaryPhone: '',
  };
}

const REQUIRED: { key: keyof NewStopFormValues; message: string }[] = [
  { key: 'distributorId', message: 'Choose a distributor.' },
  { key: 'routeId', message: "Choose one of the distributor's routes." },
  { key: 'name', message: 'Enter the customer name.' },
  { key: 'streetAddress', message: 'Enter the street address.' },
  { key: 'city', message: 'Enter the city.' },
  { key: 'state', message: 'Enter the state.' },
  { key: 'zipCode', message: 'Enter the ZIP code.' },
];

/** Customer name, full address, distributor and route are required; contact and phones are not. */
export function validateNewStop(values: NewStopFormValues): FormErrors {
  const errors: FormErrors = {};
  for (const field of REQUIRED) {
    if (!values[field.key]?.trim()) errors[field.key] = field.message;
  }
  if (values.zipCode.trim().length > 10)
    errors.zipCode = 'ZIP code must be 10 characters or fewer.';
  else if (values.zipCode.trim() && !/^\d{5}(?:-\d{4})?$/.test(values.zipCode.trim()))
    errors.zipCode = 'Enter a 5-digit ZIP code or ZIP+4 (12345-6789).';
  return errors;
}

export function toNewStopInput(values: NewStopFormValues) {
  const text = (value: string) => value.trim();
  return {
    distributorId: Number(values.distributorId),
    routeId: Number(values.routeId),
    name: text(values.name),
    streetAddress: text(values.streetAddress),
    city: text(values.city),
    state: text(values.state),
    zipCode: text(values.zipCode),
    contactName: text(values.contactName),
    primaryPhone: text(values.primaryPhone),
    secondaryPhone: text(values.secondaryPhone),
    ...(values.googlePlaceId ? { googlePlaceId: values.googlePlaceId } : {}),
  };
}
